/**
 * A session that ends while someone is using the app.
 *
 * An account suspended, or a password or email changed on another device,
 * makes the server refuse this tab's token. Every request then failed on its
 * own with the raw API text ("Session ended — please sign in again",
 * "Account suspended. Contact your administrator.") while the page stayed up,
 * and the next reload dropped the person on the sign-in page with no word of
 * why (audit A, M8). Now the first refused request announces it once, the
 * AuthProvider signs the tab out, and the sign-in page says why in plain words.
 */

export const SESSION_ENDED_EVENT = 'auth:session-ended';

/**
 * Where a 401 means "those credentials were wrong", not "your session ended":
 * a mistyped password, a wrong current password, a dead device token.
 */
const CREDENTIAL_ENDPOINTS = [
  '/auth/login',
  '/auth/password',
  '/auth/setup-password',
  '/auth/reset-password',
  '/auth/forgot-password',
  '/device-tokens/exchange',
];

/** Did this failed request just tell us the signed-in session is over? */
export function isSessionEnd(status, endpoint, sentToken) {
  if (status !== 401 || !sentToken) return false;
  const path = String(endpoint || '').split('?')[0];
  return !CREDENTIAL_ENDPOINTS.includes(path);
}

/** What the sign-in page tells the person, from the server's refusal. */
export function sessionEndedNotice(serverMessage = '') {
  const m = String(serverMessage);
  if (/suspended/i.test(m)) {
    return 'You have been signed out because your account is suspended. If you think this is a mistake, contact your department\'s technical officer.';
  }
  if (/session ended/i.test(m)) {
    return 'You have been signed out because your password or email address changed. Please sign in again.';
  }
  if (/expired/i.test(m)) {
    return 'Your session has expired. Please sign in again.';
  }
  return 'You have been signed out. Please sign in again.';
}

let announced = false;

/** Tell the app, once per page load, that the session is over. */
export function announceSessionEnded(serverMessage) {
  if (announced || typeof window === 'undefined') return;
  announced = true;
  window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { message: serverMessage || '' } }));
}

/** Tests only: forget that the session end was announced. */
export function resetSessionEndAnnouncement() {
  announced = false;
}
